import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingCart, Heart, ArrowLeft, Star, Eye } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useFavorites } from '../../context/FavoritesContext';
import Button from '../common/Button';
import Toast from '../common/Toast';
import api from '../../services/api';
import productService from '../../services/productService';


const ProductDetail = () => {
    const { id } = useParams();
    const { addToCart } = useCart();
    const { toggleFavorite, isFavorite } = useFavorites();
    const [product, setProduct] = useState(null);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedColor, setSelectedColor] = useState(0);
    const [quantity, setQuantity] = useState(1);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const [productData, categoriesData] = await Promise.all([
                    api.get(`/api/products/${id}/`),
                    productService.getCategories()
                ]);
                setProduct(productData.data);
                setCategories(categoriesData);
                setLoading(false);

                // Increment view count
                api.post(`/api/products/${id}/increment_views/`).catch(err => {
                    console.error('Error incrementing views:', err);
                });
            } catch (err) {
                console.error('Error fetching product details:', err);
                setError('Ce produit n\'est plus disponible ou une erreur est survenue.');
                setLoading(false);
            }
        };

        loadData();
    }, [id]);

    const handleAddToCart = () => {
        if (product) {
            const wasAdded = addToCart(product, quantity);
            if (wasAdded) {
                setToastMessage(`${product.name} ajouté au panier !`);
            } else {
                setToastMessage("Ce produit est déjà dans votre panier.");
            }
            setShowToast(true);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center pt-32">
                <div className="flex flex-col items-center gap-6">
                    <div className="w-16 h-16 border-4 border-brand/20 border-t-brand rounded-full animate-spin"></div>
                    <p className="text-brand-dark/60 font-medium">Récupération des détails du produit...</p>
                </div>
            </div>
        );
    }

    if (error || !product) {
        return (
            <div className="min-h-screen pt-40 px-6">
                <div className="container mx-auto max-w-2xl text-center py-24 bg-white rounded-[3rem] border border-slate-100 shadow-xl">
                    <div className="w-20 h-20 bg-red-50 text-red-500 rounded-3xl flex items-center justify-center mx-auto mb-8">
                        <Eye className="w-10 h-10 opacity-50" />
                    </div>
                    <h3 className="text-2xl font-bold text-brand-dark mb-4">Oups !</h3>
                    <p className="text-slate-500 mb-8">{error || "Produit introuvable"}</p>
                    <Link to="/categories">
                        <Button variant="primary">Retour aux catégories</Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white pt-32 pb-24">
            <div className="container mx-auto px-6">
                {/* Breadcrumbs / Back Link */}
                <Link to="/" className="inline-flex items-center gap-2 text-green-600 font-semibold mb-8 hover:gap-3 transition-all">
                    <ArrowLeft className="w-5 h-5" />
                    Retour à la boutique
                </Link>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                    {/* Image Gallery */}
                    <div className="relative group">
                        <div className="aspect-square rounded-[3rem] overflow-hidden bg-green-50 shadow-2xl">
                            <img
                                src={product.image.startsWith('http') ? product.image : `${api.defaults.baseURL}${product.image}`}
                                alt={product.name}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        {/* Tags */}
                        <div className="absolute top-8 left-8 flex flex-col gap-3">
                            <span className="px-6 py-2 bg-white/90 backdrop-blur-md text-green-900 text-xs font-bold rounded-full shadow-lg uppercase tracking-widest">
                                {(() => {
                                    const cat = categories.find(c => String(c.id) === String(product.category) || c.name === product.category);
                                    return cat ? cat.name : (product.category === 'null' || !product.category ? 'Général' : product.category);
                                })()}
                            </span>
                        </div>
                    </div>

                    {/* Product Info */}
                    <div className="flex flex-col">
                        <div className="mb-8">
                            <div className="flex items-center gap-2 mb-4 text-yellow-500">
                                <div className="flex">
                                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                                </div>
                                <span className="text-sm font-bold text-green-950">(48 avis clients)</span>
                            </div>
                            <h1 className="text-4xl md:text-5xl font-extrabold text-green-950 mb-4 leading-tight">
                                {product.name}
                            </h1>
                            <div className="flex items-center gap-4 mb-6">
                                <p className="text-3xl font-bold text-green-600">{Math.floor(product.price).toLocaleString('fr-FR')} fcfa</p>
                                <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-sm ${product.stock > 0
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-600'
                                    }`}>

                                </span>
                            </div>
                            <p className="text-lg text-slate-600 leading-relaxed max-w-xl">
                                {product.description}
                            </p>
                        </div>

                        {/* Options */}
                        <div className="space-y-8 mb-10">
                            {/* Color Selector */}
                            {product.colors && product.colors.length > 0 && (
                                <div>
                                    <p className="text-sm font-bold text-green-950 uppercase tracking-widest mb-4">Coloris disponible</p>
                                    <div className="flex gap-4">
                                        {product.colors.map((color, index) => (
                                            <button
                                                key={index}
                                                onClick={() => setSelectedColor(index)}
                                                className={`w-12 h-12 rounded-full border-4 transition-all ${selectedColor === index ? 'border-green-600 scale-110 shadow-lg' : 'border-transparent hover:scale-105'}`}
                                                style={{ backgroundColor: color }}
                                                aria-label={`Couleur ${index + 1}`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Quantity */}
                            <div>
                                <p className="text-sm font-bold text-green-950 uppercase tracking-widest mb-4">Quantité</p>
                                <div className="flex items-center gap-6">
                                    <div className="flex items-center border-2 border-green-100 rounded-full h-14 bg-green-50/50">
                                        <button
                                            onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                            disabled={product.stock === 0}
                                            className="w-14 h-full flex items-center justify-center text-xl font-bold text-green-900 hover:text-green-600 disabled:opacity-30 transition-colors"
                                        >
                                            -
                                        </button>
                                        <span className={`w-10 text-center font-bold text-green-950 text-lg ${product.stock === 0 ? 'opacity-30' : ''}`}>{product.stock === 0 ? 0 : quantity}</span>
                                        <button
                                            onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                                            disabled={product.stock === 0 || quantity >= product.stock}
                                            className="w-14 h-full flex items-center justify-center text-xl font-bold text-green-900 hover:text-green-600 disabled:opacity-30 transition-colors"
                                        >
                                            +
                                        </button>
                                    </div>
                                    <button className="flex items-center gap-2 text-green-700 font-bold hover:text-green-500 transition-colors decoration-2 underline-offset-4 hover:underline">
                                        <Heart className="w-5 h-5" />
                                        Ajouter aux favoris
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row gap-4 mb-12">
                            <Button
                                variant="primary"
                                className={`h-16 px-10 text-lg flex-1 ${product.stock === 0 ? 'opacity-50 grayscale cursor-not-allowed' : ''}`}
                                onClick={() => product.stock > 0 && handleAddToCart()}
                                disabled={product.stock === 0}
                            >
                                <ShoppingCart className="w-6 h-6 mr-2" />
                                {product.stock === 0 ? 'Rupture de Stock' : 'Ajouter au Panier'}
                            </Button>
                            <button
                                onClick={() => product && toggleFavorite(product)}
                                className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-all border-2 ${isFavorite(product?.id)
                                    ? 'bg-brand/10 border-brand text-brand'
                                    : 'bg-white border-green-900/10 text-green-900 hover:border-green-900'
                                    }`}
                            >
                                <Heart className={`w-6 h-6 ${isFavorite(product?.id) ? 'fill-current' : ''}`} />
                            </button>
                        </div>

                    </div>
                </div>

            </div>
            <Toast
                message={toastMessage}
                isOpen={showToast}
                onClose={() => setShowToast(false)}
            />
        </div>
    );
};

export default ProductDetail;
